import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useAuth } from './AuthContext'
import { useGuestCart, GuestCartItem } from '../hooks/useGuestCart'

interface Product {
  id: string
  name: string
  price: number
  image_urls: string[]
  stock: number
}

interface ProductVariant {
  id: string
  product_id: string
  variant_name: string
  variant_value: string
  price: number
  stock_quantity: number
  created_at: string
}

interface AuthCartItem {
  id: string
  user_id: string
  product_id: string
  variant_id: string | null
  quantity: number
  created_at: string
  updated_at: string
  product?: Product
  variant?: ProductVariant
}

// Union type for cart items - works for both authenticated and guest carts
export type CartItem = AuthCartItem | GuestCartItem

interface CartContextType {
  cartItems: CartItem[]
  loading: boolean
  error: string | null
  cartCount: number
  cartTotal: number
  isGuest: boolean
  addToCart: (
    productId: string,
    variantId?: string | null,
    quantity?: number,
    productData?: Product,
    variantData?: ProductVariant
  ) => Promise<{ success: boolean; error?: string }>
  updateQuantity: (productId: string, variantId: string | null, quantity: number) => Promise<{ success: boolean; error?: string }>
  removeFromCart: (productId: string, variantId: string | null) => Promise<{ success: boolean; error?: string }>
  clearCart: () => Promise<{ success: boolean; error?: string }>
  refreshCart: () => Promise<void>
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { user, session } = useAuth()
  const [authCartItems, setAuthCartItems] = useState<AuthCartItem[]>([])
  const [authLoading, setAuthLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Guest cart hook
  const {
    guestCartItems,
    guestCartCount,
    guestCartTotal,
    loading: guestLoading,
    addToGuestCart,
    updateGuestCartQuantity,
    removeFromGuestCart,
    clearGuestCart
  } = useGuestCart()

  // Determine if user is a guest
  const isGuest = !user

  // Use appropriate cart based on auth state
  const cartItems: CartItem[] = isGuest ? guestCartItems : authCartItems
  const loading = isGuest ? guestLoading : authLoading

  // Calculate cart count and total
  const cartCount = isGuest ? guestCartCount : authCartItems.reduce((sum, item) => sum + item.quantity, 0)
  const cartTotal = isGuest ? guestCartTotal : authCartItems.reduce((sum, item) => {
    // Use variant price if variant exists, otherwise use product price
    const price = item.variant?.price ?? item.product?.price ?? 0
    return sum + (price * item.quantity)
  }, 0)

  // Fetch authenticated user cart items with product and variant details
  const fetchCartItems = useCallback(async () => {
    if (!user || !session) {
      setAuthCartItems([])
      return
    }

    setAuthLoading(true)
    setError(null)

    try {
      // Fetch cart items
      const { data: cartData, error: cartError } = await supabase
        .from('cart_items')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (cartError) throw cartError

      if (!cartData || cartData.length === 0) {
        setAuthCartItems([])
        return
      }

      // Fetch product details for each cart item
      const productIds = cartData.map(item => item.product_id)
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id, name, price, image_urls, stock')
        .in('id', productIds)

      if (productsError) throw productsError

      // Fetch variant details for cart items that have variants
      const variantIds = cartData
        .map(item => item.variant_id)
        .filter((id): id is string => id !== null)

      let variantsData: ProductVariant[] = []
      if (variantIds.length > 0) {
        const { data: vData, error: variantsError } = await supabase
          .from('product_variants')
          .select('*')
          .in('id', variantIds)

        if (variantsError) throw variantsError
        variantsData = vData || []
      }

      // Combine cart items with product and variant data
      const itemsWithProducts = cartData.map(cartItem => ({
        ...cartItem,
        product: productsData?.find(p => p.id === cartItem.product_id),
        variant: cartItem.variant_id
          ? variantsData.find(v => v.id === cartItem.variant_id)
          : undefined
      }))

      setAuthCartItems(itemsWithProducts)
    } catch (err) {
      console.error('Error fetching cart items:', err)
      setError('Failed to load cart items')
    } finally {
      setAuthLoading(false)
    }
  }, [user, session])

  // Fetch cart when user changes
  useEffect(() => {
    fetchCartItems()
  }, [fetchCartItems])

  // Add item to cart (upsert - updates quantity if exists)
  // For guest users, productData and variantData are required
  const addToCart = async (
    productId: string,
    variantId?: string | null,
    quantity: number = 1,
    productData?: Product,
    variantData?: ProductVariant
  ): Promise<{ success: boolean; error?: string }> => {
    // Handle guest cart
    if (!user) {
      if (!productData) {
        return { success: false, error: 'Product data required for guest cart' }
      }
      return addToGuestCart(
        productId,
        variantId || null,
        quantity,
        productData,
        variantData ? {
          id: variantData.id,
          variant_name: variantData.variant_name,
          variant_value: variantData.variant_value,
          price: variantData.price,
          stock_quantity: variantData.stock_quantity
        } : undefined
      )
    }

    // Handle authenticated cart
    try {
      // Check if item already exists in cart (matching both productId and variantId)
      const existingItem = authCartItems.find(item =>
        item.product_id === productId &&
        item.variant_id === (variantId || null)
      )

      if (existingItem) {
        // Update quantity
        const newQuantity = existingItem.quantity + quantity

        // Check stock before updating (use variant stock if variant exists)
        const availableStock = existingItem.variant
          ? existingItem.variant.stock_quantity
          : (existingItem.product?.stock || 0)

        if (newQuantity > availableStock) {
          return { success: false, error: `Only ${availableStock} items available in stock` }
        }

        // Build query for update
        let updateQuery = supabase
          .from('cart_items')
          .update({ quantity: newQuantity })
          .eq('user_id', user.id)
          .eq('product_id', productId)

        if (variantId) {
          updateQuery = updateQuery.eq('variant_id', variantId)
        } else {
          updateQuery = updateQuery.is('variant_id', null)
        }

        const { error: updateError } = await updateQuery

        if (updateError) throw updateError
      } else {
        // Insert new cart item
        const { error: insertError } = await supabase
          .from('cart_items')
          .insert({
            user_id: user.id,
            product_id: productId,
            variant_id: variantId || null,
            quantity: quantity
          })

        if (insertError) throw insertError
      }

      // Refresh cart
      await fetchCartItems()
      return { success: true }
    } catch (err) {
      console.error('Error adding to cart:', err)
      return { success: false, error: 'Failed to add item to cart' }
    }
  }

  // Update item quantity
  const updateQuantity = async (productId: string, variantId: string | null, quantity: number): Promise<{ success: boolean; error?: string }> => {
    // Handle guest cart
    if (!user) {
      return updateGuestCartQuantity(productId, variantId, quantity)
    }

    if (quantity < 1) {
      return removeFromCart(productId, variantId)
    }

    try {
      // Check stock before updating
      const cartItem = authCartItems.find(item =>
        item.product_id === productId &&
        item.variant_id === variantId
      )

      // Use variant stock if variant exists, otherwise use product stock
      const availableStock = cartItem?.variant
        ? cartItem.variant.stock_quantity
        : (cartItem?.product?.stock || 0)

      if (quantity > availableStock) {
        return { success: false, error: `Only ${availableStock} items available in stock` }
      }

      // Build query for update
      let updateQuery = supabase
        .from('cart_items')
        .update({ quantity })
        .eq('user_id', user.id)
        .eq('product_id', productId)

      if (variantId) {
        updateQuery = updateQuery.eq('variant_id', variantId)
      } else {
        updateQuery = updateQuery.is('variant_id', null)
      }

      const { error: updateError } = await updateQuery

      if (updateError) throw updateError

      // Refresh cart
      await fetchCartItems()
      return { success: true }
    } catch (err) {
      console.error('Error updating quantity:', err)
      return { success: false, error: 'Failed to update quantity' }
    }
  }

  // Remove item from cart
  const removeFromCart = async (productId: string, variantId: string | null): Promise<{ success: boolean; error?: string }> => {
    // Handle guest cart
    if (!user) {
      return removeFromGuestCart(productId, variantId)
    }

    try {
      // Build query for delete
      let deleteQuery = supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', productId)

      if (variantId) {
        deleteQuery = deleteQuery.eq('variant_id', variantId)
      } else {
        deleteQuery = deleteQuery.is('variant_id', null)
      }

      const { error: deleteError } = await deleteQuery

      if (deleteError) throw deleteError

      // Refresh cart
      await fetchCartItems()
      return { success: true }
    } catch (err) {
      console.error('Error removing from cart:', err)
      return { success: false, error: 'Failed to remove item from cart' }
    }
  }

  // Clear entire cart
  const clearCart = async (): Promise<{ success: boolean; error?: string }> => {
    // Handle guest cart
    if (!user) {
      return clearGuestCart()
    }

    try {
      const { error: deleteError } = await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id)

      if (deleteError) throw deleteError

      setAuthCartItems([])
      return { success: true }
    } catch (err) {
      console.error('Error clearing cart:', err)
      return { success: false, error: 'Failed to clear cart' }
    }
  }

  // Refresh cart manually
  const refreshCart = async () => {
    await fetchCartItems()
  }

  const value = {
    cartItems,
    loading,
    error,
    cartCount,
    cartTotal,
    isGuest,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
